import { useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { ListShell } from "@/components/shared/ListShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  useGlobalMedicines,
  useCreateGlobalMedicine,
  useUpdateGlobalMedicine,
  useDeleteGlobalMedicine,
} from "@/hooks/useAdminMedicines";
import { Plus, Search, Edit2, Trash2, Pill } from "lucide-react";
import { toast } from "sonner";
import type { Medicine } from "@postnatal-care/shared-types";

export const MedicinesPage = () => {
  const { data: medicines, isLoading, error, refetch } = useGlobalMedicines();
  const createMut = useCreateGlobalMedicine();
  const updateMut = useUpdateGlobalMedicine();
  const deleteMut = useDeleteGlobalMedicine();

  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMedicine, setEditingMedicine] = useState<Medicine | null>(null);

  // Form states
  const [nameEn, setNameEn] = useState("");
  const [nameMl, setNameMl] = useState("");
  const [descEn, setDescEn] = useState("");
  const [descMl, setDescMl] = useState("");
  const [category, setCategory] = useState("");
  const [isActive, setIsActive] = useState(true);

  const openAddDialog = () => {
    setEditingMedicine(null);
    setNameEn("");
    setNameMl("");
    setDescEn("");
    setDescMl("");
    setCategory("");
    setIsActive(true);
    setDialogOpen(true);
  };

  const openEditDialog = (med: Medicine) => {
    setEditingMedicine(med);
    setNameEn(med.nameEn);
    setNameMl(med.nameMl);
    setDescEn(med.descriptionEn ?? "");
    setDescMl(med.descriptionMl ?? "");
    setCategory(med.category ?? "");
    setIsActive(med.isActive);
    setDialogOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nameEn || !nameMl) {
      return toast.error("English and Malayalam names are required");
    }

    try {
      if (editingMedicine) {
        await updateMut.mutateAsync({
          id: editingMedicine.id,
          data: {
            nameEn,
            nameMl,
            descriptionEn: descEn || undefined,
            descriptionMl: descMl || undefined,
            category: category || undefined,
            isActive,
          },
        });
        toast.success("Medicine updated successfully");
      } else {
        await createMut.mutateAsync({
          nameEn,
          nameMl,
          descriptionEn: descEn || undefined,
          descriptionMl: descMl || undefined,
          category: category || undefined,
          isActive,
        });
        toast.success("Medicine added successfully");
      }
      setDialogOpen(false);
    } catch (err: any) {
      toast.error(err.message || "Failed to save medicine");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this medicine globally?"))
      return;
    try {
      await deleteMut.mutateAsync(id);
      toast.success("Medicine deleted successfully");
    } catch (err: any) {
      toast.error(err.message || "Failed to delete medicine");
    }
  };

  const filteredMedicines = (medicines ?? []).filter(
    (m) =>
      m.nameEn.toLowerCase().includes(search.toLowerCase()) ||
      m.nameMl.toLowerCase().includes(search.toLowerCase()) ||
      (m.category && m.category.toLowerCase().includes(search.toLowerCase())),
  );

  return (
    <AppLayout title="Postnatal Medicines Directory">
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search global medicines directory…"
            className="pl-8"
          />
        </div>
        <Button onClick={openAddDialog} className="gap-2">
          <Plus className="w-4 h-4" /> Add Global Medicine
        </Button>
      </div>

      <ListShell
        isLoading={isLoading}
        error={error}
        isEmpty={filteredMedicines.length === 0}
        onRetry={() => refetch()}
        emptyTitle="No Medicines Found"
        emptyDescription="Get started by adding a recovery medicine to the directory."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredMedicines.map((med) => (
            <div
              key={med.id}
              className="bg-card rounded-card border border-border p-5 flex flex-col justify-between gap-4"
            >
              <div className="space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Pill className="w-4 h-4 text-primary shrink-0" />
                    <h3 className="font-semibold text-sm">{med.nameEn}</h3>
                  </div>
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full font-semibold capitalize ${
                      med.isActive
                        ? "bg-emerald-500/10 text-emerald-600"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {med.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground font-medium">
                  {med.nameMl}
                </p>
                {med.category && (
                  <p className="text-[11px] bg-muted/60 text-muted-foreground px-2 py-0.5 rounded w-fit">
                    {med.category}
                  </p>
                )}
                <div className="space-y-1.5 pt-2">
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    <span className="font-medium">EN:</span>{" "}
                    {med.descriptionEn || "No English description"}
                  </p>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    <span className="font-medium">ML:</span>{" "}
                    {med.descriptionMl || "No Malayalam description"}
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-2 border-t pt-3 mt-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => openEditDialog(med)}
                >
                  <Edit2 className="w-3.5 h-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive"
                  onClick={() => handleDelete(med.id)}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </ListShell>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <form onSubmit={handleSave} className="space-y-4">
            <DialogHeader>
              <DialogTitle>
                {editingMedicine
                  ? "Edit Global Medicine"
                  : "Add Global Medicine"}
              </DialogTitle>
              <DialogDescription>
                Configure the master properties of this medicine. It will be
                available for centres to select.
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="nameEn">Name (English)</Label>
                <Input
                  id="nameEn"
                  placeholder="e.g. Dhanwantharam Kuzhambu"
                  value={nameEn}
                  onChange={(e) => setNameEn(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="nameMl">Name (Malayalam)</Label>
                <Input
                  id="nameMl"
                  placeholder="e.g. ധന്വന്തരം കുഴമ്പ്"
                  value={nameMl}
                  onChange={(e) => setNameMl(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="category">Category / Group (Optional)</Label>
              <Input
                id="category"
                placeholder="e.g. Ayurvedic Oils / Tablets"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="descEn">Description (English)</Label>
              <Textarea
                id="descEn"
                placeholder="Details, uses, etc..."
                value={descEn}
                onChange={(e) => setDescEn(e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="descMl">Description (Malayalam)</Label>
              <Textarea
                id="descMl"
                placeholder="വിശദാംശങ്ങൾ..."
                value={descMl}
                onChange={(e) => setDescMl(e.target.value)}
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="active"
                checked={isActive}
                onCheckedChange={setIsActive}
              />
              <Label htmlFor="active">Active (available for selection)</Label>
            </div>

            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit">
                {editingMedicine ? "Save changes" : "Add Medicine"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};
